import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { blockedUserCount, tenantsOf, type McpTenantDetail, type McpTenantList } from "../../api/mcp";
import { Breadcrumbs } from "../../components/Layout";
import { LoadFailed, NotSeenRecently, ResourceView } from "../../components/states";
import { Notice, Page, Panel, Table, Td, Th } from "../../components/ui";
import type { AppConfig } from "../../config";
import { useResource } from "../../hooks/useResource";
import { Button, Input } from "../../f7";

/**
 * Users blocked at one merchant.
 *
 * The per-user rows are MOVING from `GET /admin/tenants` onto tenant detail,
 * and dev has already moved. So this reads detail first and falls back to the
 * fleet list, which keeps it working in both environments while the rollout
 * finishes — and once prod follows, the fleet read here can go entirely and
 * the page drops to one request.
 *
 * Existence and 404 come from `GET /admin/tenant/{ref}`, the per-merchant
 * route. Checking one specific email is its own route,
 * `GET /admin/tenant/{ref}/user/{email}`, which is authoritative for one
 * person and does not depend on either list.
 */
export function TenantUsers({ config }: { config: AppConfig }) {
  const { ref = "" } = useParams();
  const enc = encodeURIComponent(ref);
  const tenant = useResource<McpTenantDetail>(config, `/mcp/admin/tenant/${enc}`, [ref]);
  const list = useResource<McpTenantList>(config, "/mcp/admin/tenants");
  const [email, setEmail] = useState("");

  const s = tenant.state;

  return (
    <Page title={<span className="ref">{ref}</span>} subtitle="Users blocked at this merchant.">
      <Breadcrumbs
        trail={[
          { label: "Overview", to: "/" },
          { label: "Tenants", to: "/tenants" },
          { label: ref, to: `/tenants/${encodeURIComponent(ref)}` },
          { label: "Users" },
        ]}
      />

      <ResourceView
        state={s}
        what="this merchant"
        onRetry={tenant.reload}
        notFound={
          <NotSeenRecently
            reference={ref}
            actions={<Lookup ref={ref} email={email} setEmail={setEmail} />}
          />
        }
      />

      {(s.kind === "ready" || s.kind === "notfound") && (
        <>
          {s.kind === "ready" && <Lookup ref={ref} email={email} setEmail={setEmail} />}

          <ResourceView
            state={list.state}
            what="the blocked-user list"
            onRetry={list.reload}
            notFound={
              <LoadFailed what="blocked users" message="Route not found on this gateway." />
            }
          />

          {list.state.kind === "ready" &&
            (() => {
              const row = tenantsOf(list.state.data).find((x) => x.business_reference === ref);
              const everywhere = list.state.data.users_blocked_across_all_merchants ?? [];
              /**
               * DETAIL FIRST, then the fleet list.
               *
               * The names are moving from the list onto tenant detail and dev
               * has already made the move, so reading only the list gave `[]`
               * and this page announced "No users blocked here" while the
               * merchant list showed a count of three. Silence that contradicts
               * another page is worse than either answer alone.
               */
              const detail = s.kind === "ready" ? s.data.blocked_users : undefined;
              const blocked = detail ?? row?.blocked_users ?? [];
              // What the list SAYS is blocked here, whichever shape it sent.
              const counted = row ? blockedUserCount(row) : 0;

              return (
                <>
                  {blocked.length === 0 && counted === 0 && (
                    <Notice title="No users blocked here">
                      Nobody has a block row for this merchant specifically.
                    </Notice>
                  )}

                  {/* A count with no names to put against it. Neither response
                      carried them, so the page says the number and refuses to
                      imply the list is empty. */}
                  {blocked.length === 0 && counted > 0 && (
                    <Notice tone="amber" title={`${counted} user${counted === 1 ? "" : "s"} blocked here`}>
                      <p>
                        The merchant list reports {counted} block
                        {counted === 1 ? "" : "s"} at this merchant, and neither response
                        carried the addresses — so they cannot be listed. Check a specific
                        address with the lookup above; it reads{" "}
                        <span className="ref">GET /admin/tenant/{"{ref}"}/user/{"{email}"}</span>,
                        which is authoritative for one person.
                      </p>
                    </Notice>
                  )}

                  {blocked.length > 0 && (
                    <Table
                      head={
                        <tr>
                          <Th>Email</Th>
                          <Th>Scope</Th>
                        </tr>
                      }
                    >
                      {blocked.map((e) => (
                        <tr key={e}>
                          <Td>
                            <Link
                              to={`/tenants/${encodeURIComponent(ref)}/users/${encodeURIComponent(e)}`}
                              className="ref"
                            >
                              {e}
                            </Link>
                          </Td>
                          <Td>
                            {everywhere.includes(e) ? (
                              <span className="text-xs text-amber-700 dark:text-amber-400">
                                also blocked at every merchant — unblocking here will not lift that
                              </span>
                            ) : (
                              <span className="text-xs text-slate-500 dark:text-slate-400">
                                this merchant only
                              </span>
                            )}
                          </Td>
                        </tr>
                      ))}
                    </Table>
                  )}
                </>
              );
            })()}
        </>
      )}
    </Page>
  );
}

/**
 * Something@something.tld, and no spaces. Deliberately loose: the API is the
 * authority on what an email is, and this only stops a stray letter reaching a
 * page that answers "Allowed" and offers Block for it (DA-3590).
 */
export function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function Lookup({
  ref: reference,
  email,
  setEmail,
}: {
  ref: string;
  email: string;
  setEmail: (v: string) => void;
}) {
  // Same trimmed value in the guard and in the URL.
  const typed = email.trim();
  return (
    <Panel className="mb-4 p-4">
      <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
        Check whether any user would be refused at this merchant — including
        someone not in the blocked list yet. This hits{" "}
        <span className="ref">GET /admin/tenant/{"{ref}"}/user/{"{email}"}</span>.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {/* Width on a wrapper — F7's Input is a `w-full` span. */}
        <div className="w-72">
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@example.com"
            aria-label="Email to check"
          />
        </div>
        {/* F7's Button, routed by the document-level link handler in
            useInternalLinks — not a Link hand-painted to look like one. The
            painted version carried a 2px radius and its own padding, so it sat
            a different height from the Input beside it. */}
        {/* Same trimmed value in the guard and in the URL — `email` alone is
            truthy for a string of spaces, which would have built a link the
            disabled state was meanwhile refusing. */}
        <Button
          href={
            looksLikeEmail(typed)
              ? `/tenants/${encodeURIComponent(reference)}/users/${encodeURIComponent(
                  typed.toLowerCase(),
                )}`
              : undefined
          }
          disabled={!looksLikeEmail(typed)}
        >
          Check
        </Button>
      </div>
      {typed && !looksLikeEmail(typed) && (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400">
          <span className="ref">{typed}</span> is not an email address. Blocks are kept per
          email, so a lookup of anything else can only ever say "allowed".
        </p>
      )}
      <p className="mt-2 text-[11px] text-slate-400 dark:text-slate-500">
        Emails are stored lowercase, so that is how it will be checked.
      </p>
    </Panel>
  );
}
